import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, X, Upload } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductsManager = () => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newProductItem, setNewProductItem] = useState('');

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast({
        title: "Errore",
        description: "Impossibile caricare i prodotti",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSave = async () => {
    try {
      // Validation
      if (!editingProduct.brand || !editingProduct.category) {
        toast({
          title: "Errore",
          description: "Brand e categoria sono obbligatori",
          variant: "destructive"
        });
        return;
      }

      const productData = {
        ...editingProduct,
        products: editingProduct.products || []
      };

      if (editingProduct.id && editingProduct.id !== 'new') {
        // Update
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        toast({ title: "✅ Salvato!", description: "Prodotto aggiornato con successo" });
      } else {
        // Create
        delete productData.id;
        await axios.post(`${API}/products`, productData);
        toast({ title: "✅ Creato!", description: "Nuovo prodotto aggiunto" });
      }
      
      setIsDialogOpen(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast({
        title: "Errore",
        description: error.response?.data?.detail || "Impossibile salvare il prodotto",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast({ title: "✅ Eliminato!", description: "Prodotto rimosso" });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare il prodotto",
        variant: "destructive"
      });
    }
  };

  const openEditDialog = (product = null) => {
    if (product) {
      setEditingProduct({
        ...product,
        products: Array.isArray(product.products) ? product.products : []
      });
    } else {
      setEditingProduct({
        id: 'new',
        brand: '',
        category: '',
        products: [],
        image: '',
        description: '',
        featured: true,
        order: products.length + 1
      });
    }
    setIsDialogOpen(true);
    setNewProductItem('');
  };

  const updateField = (field, value) => {
    setEditingProduct({...editingProduct, [field]: value});
  };

  const addProductItem = () => {
    if (newProductItem && newProductItem.trim()) {
      const currentProducts = editingProduct.products || [];
      updateField('products', [...currentProducts, newProductItem.trim().toUpperCase()]);
      setNewProductItem('');
    }
  };

  const removeProductItem = (index) => {
    const newProducts = editingProduct.products.filter((_, i) => i !== index);
    updateField('products', newProducts);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ fontSize: '18px', color: '#64748b' }}>Caricamento prodotti...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1e293b',
            marginBottom: '8px'
          }}>
            🔥 Gestione Prodotti SVAPO
          </h2>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            Totale prodotti: {products.length} • Aggiungi, modifica o elimina prodotti
          </p>
        </div>
        <Button 
          onClick={() => openEditDialog()} 
          style={{
            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: '600'
          }}
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuovo Prodotto
        </Button>
      </div>

      {/* Products Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '24px'
      }}>
        {products.map((product) => (
          <Card key={product.id} style={{
            padding: '20px',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            transition: 'all 0.2s',
            cursor: 'pointer'
          }}>
            {/* Product Image */}
            <div style={{
              width: '100%',
              height: '150px',
              background: 'white',
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e2e8f0',
              padding: '10px'
            }}>
              {product.image ? (
                <img 
                  src={product.image} 
                  alt={product.brand}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <div style={{
                  color: '#94a3b8',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  Nessuna immagine
                </div>
              )}
            </div>
            
            {/* Product Info */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '4px'
              }}>
                {product.brand}
              </h3>
              <p style={{
                fontSize: '13px',
                color: '#64748b',
                marginBottom: '12px'
              }}>
                {product.category}
              </p>
              
              {product.featured && (
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: '#fef3c7',
                  color: '#f59e0b',
                  fontSize: '11px',
                  fontWeight: '600',
                  borderRadius: '4px',
                  marginBottom: '12px'
                }}>
                  ⭐ IN EVIDENZA
                </span>
              )}
              
              {product.products && product.products.length > 0 && (
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginTop: '8px'
                }}>
                  {product.products.length} prodotti disponibili
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '8px',
              paddingTop: '12px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button 
                onClick={() => openEditDialog(product)}
                variant="outline"
                style={{ flex: 1, fontSize: '13px' }}
              >
                <Edit className="w-4 h-4 mr-1" />
                Modifica
              </Button>
              <Button 
                onClick={() => handleDelete(product.id)}
                variant="outline"
                style={{
                  color: '#ef4444',
                  borderColor: '#fecaca',
                  fontSize: '13px'
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontSize: '22px', fontWeight: '700' }}>
              {editingProduct?.id === 'new' ? '➕ Nuovo Prodotto' : '✏️ Modifica Prodotto'}
            </DialogTitle>
          </DialogHeader>
          
          {editingProduct && (
            <div style={{ padding: '20px 0' }}>
              {/* Brand */}
              <div style={{ marginBottom: '20px' }}>
                <Label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  Brand *
                </Label>
                <Input
                  value={editingProduct.brand || ''}
                  onChange={(e) => updateField('brand', e.target.value)}
                  placeholder="es. KIWI, ELFBAR, RELX"
                  style={{ fontSize: '15px', padding: '12px' }}
                />
              </div>

              {/* Category */}
              <div style={{ marginBottom: '20px' }}>
                <Label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  Categoria *
                </Label>
                <Input
                  value={editingProduct.category || ''}
                  onChange={(e) => updateField('category', e.target.value)}
                  placeholder="es. Pods & Resistenze, Kit & Pods"
                  style={{ fontSize: '15px', padding: '12px' }}
                />
              </div>

              {/* Image URL */}
              <div style={{ marginBottom: '20px' }}>
                <Label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  URL Immagine
                </Label>
                <Input
                  value={editingProduct.image || ''}
                  onChange={(e) => updateField('image', e.target.value)}
                  placeholder="https://..."
                  style={{ fontSize: '15px', padding: '12px' }}
                />
                {editingProduct.image && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <img 
                      src={editingProduct.image} 
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '150px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Featured */}
              <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="checkbox"
                  checked={editingProduct.featured || false}
                  onChange={(e) => updateField('featured', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <Label style={{ fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                  ⭐ Metti in evidenza sulla homepage
                </Label>
              </div>

              {/* Products List */}
              <div style={{ marginBottom: '20px' }}>
                <Label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  Prodotti Disponibili
                </Label>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                  Lista dei prodotti specifici di questo brand (es. KIWI GO+, KIWI AIR, ecc.)
                </p>
                
                {/* Add Product Item */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <Input
                    value={newProductItem}
                    onChange={(e) => setNewProductItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addProductItem()}
                    placeholder="Nome prodotto (es. KIWI GO+)"
                    style={{ flex: 1, fontSize: '14px' }}
                  />
                  <Button onClick={addProductItem} type="button">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Products List */}
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '8px'
                }}>
                  {editingProduct.products && editingProduct.products.length > 0 ? (
                    editingProduct.products.map((item, index) => (
                      <div
                        key={`product-${editingProduct.id}-${item}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: '#f8fafc',
                          borderRadius: '6px',
                          marginBottom: '6px'
                        }}
                      >
                        <span style={{ fontSize: '14px', color: '#1e293b' }}>
                          {item}
                        </span>
                        <Button
                          onClick={() => removeProductItem(index)}
                          variant="ghost"
                          size="sm"
                          style={{ padding: '4px', color: '#ef4444' }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p style={{
                      textAlign: 'center',
                      color: '#94a3b8',
                      fontSize: '13px',
                      padding: '20px'
                    }}>
                      Nessun prodotto aggiunto
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                <Button
                  onClick={() => setIsDialogOpen(false)}
                  variant="outline"
                  style={{ flex: 1 }}
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSave}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)'
                  }}
                >
                  💾 Salva Prodotto
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Empty State */}
      {products.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#f8fafc',
          borderRadius: '12px',
          border: '2px dashed #cbd5e1'
        }}>
          <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '16px' }}>
            Nessun prodotto ancora aggiunto
          </p>
          <Button onClick={() => openEditDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi il primo prodotto
          </Button>
        </div>
      )}
    </div>
  );
};
